import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { FloppyDisk, FilePdf, FileXls, CheckCircle, Plus, Trash, PencilSimple, MagnifyingGlass, X, UsersThree } from "@phosphor-icons/react";
import { downloadJsonFile } from "../../lib/downloads";

const CORRECTIVE_ACTIONS = ["None", "Complaint", "CAPA", "Recall", "FSCA", "Other"];

const EMPTY = {
  supplier: "", product: "", variant_ref: "", batch_lot: "", udi_di: "", udi_pi: "", serial_number: "",
  purchase_date: "", sale_date: "", customer: "", customer_address: "", country: "",
  quantity: "", invoice_ref: "", comments: "", corrective_action: "None", corrective_comments: "",
};

const CA_STYLE = {
  None: "bg-zinc-50 text-zinc-600 border-zinc-200",
  Complaint: "bg-amber-50 text-amber-800 border-amber-200",
  CAPA: "bg-blue-50 text-blue-800 border-blue-200",
  Recall: "bg-orange-50 text-orange-800 border-orange-200",
  FSCA: "bg-red-50 text-red-800 border-red-200",
  Other: "bg-zinc-50 text-zinc-700 border-zinc-300",
};

export default function TraceTab({ project, role = "manufacturer", deviceId, device }) {
  const t = useT();
  const [records, setRecords] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [search, setSearch] = useState("");
  const [fAction, setFAction] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [busy, setBusy] = useState("");
  const [affected, setAffected] = useState(null);

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([
      api.get(`/projects/${project.id}/trace`, { params: deviceId ? { device_id: deviceId } : {} }),
      api.get("/user/profile").catch(() => ({ data: {} })),
    ]);
    setRecords(r.data || []);
    setCompanyName(p.data.company_name || "");
  }, [project?.id, deviceId]);

  useEffect(() => { if (project?.id) load(); }, [project?.id, load]);

  const suppliers = useMemo(() => {
    const set = new Set(records.map((r) => r.supplier).filter(Boolean));
    if (companyName) set.add(companyName);
    return [...set].sort();
  }, [records, companyName]);

  const countries = useMemo(
    () => [...new Set(records.map((r) => r.country).filter(Boolean))].sort(),
    [records]
  );

  const variantOptions = useMemo(() => (Array.isArray(device?.variants) ? device.variants : [])
    .map((raw) => {
      const item = typeof raw === "string" ? { name: raw } : (raw || {});
      const name = item.name || item.model || item.label || item.variant || "";
      const model = item.model || "";
      const udi = item.udi_di || item.udiDi || "";
      return { name, model, udi, ref: JSON.stringify([name.trim(), model.trim(), udi.trim()]) };
    })
    .filter((item) => item.name), [device?.variants]);

  const stats = useMemo(() => ({
    total: records.length,
    safety: records.filter((r) => r.corrective_action && r.corrective_action !== "None").length,
    countries: countries.length,
  }), [records, countries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((r) => {
      if (fAction && r.corrective_action !== fAction) return false;
      if (fCountry && r.country !== fCountry) return false;
      if (q && !`${r.ref} ${r.supplier} ${r.product} ${r.variant_name} ${r.batch_lot} ${r.udi_di} ${r.udi_pi} ${r.serial_number} ${r.customer} ${r.invoice_ref}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [records, search, fAction, fCountry]);

  function startNew() {
    setEditing("new");
    setDraft({ ...EMPTY, product: device?.name || "", udi_di: device?.udi_di || "" });
    setOkMsg("");
  }
  function startEdit(rec) { setEditing(rec.id); setDraft({ ...EMPTY, ...rec }); setOkMsg(""); }

  async function save() {
    if (!draft.product.trim()) return;
    setSaving(true);
    setOkMsg("");
    try {
      const payload = { ...EMPTY, device_id: deviceId, variant_ref: draft.variant_ref || "" };
      Object.keys(EMPTY).forEach((k) => { payload[k] = draft[k] || EMPTY[k]; });
      if (device) {
        payload.product = device.name || payload.product;
        const selected = variantOptions.find((option) => option.ref === payload.variant_ref);
        payload.udi_di = selected?.udi || device.udi_di || payload.udi_di;
      }
      if (editing === "new") await api.post(`/projects/${project.id}/trace`, payload);
      else await api.put(`/projects/${project.id}/trace/${editing}`, payload);
      setOkMsg(t("qms.saved"));
      setEditing(null);
      await load();
    } finally { setSaving(false); }
  }

  async function remove(rec) {
    if (!window.confirm(t("qms.confirm_delete"))) return;
    await api.delete(`/projects/${project.id}/trace/${rec.id}`);
    if (editing === rec.id) setEditing(null);
    await load();
  }

  async function downloadFile(path, filename, key) {
    setBusy(key);
    try {
      const scopedPath = deviceId && path.includes("/trace/export/")
        ? `${path}${path.includes("?") ? "&" : "?"}device_id=${encodeURIComponent(deviceId)}`
        : path;
      const res = await fetch(`${API}${scopedPath}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rg_token")}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setBusy(""); }
  }

  function exportTraceJson(record = null) {
    const base = {
      schema: "airegulatory.traceability-source.v1",
      document_type: "traceability_source_data",
      exported_at: new Date().toISOString(),
      project: { id: project.id, name: project.name },
      limitations: [
        "Exportación de los datos de trazabilidad guardados en la aplicación.",
        "No valida la exactitud de lotes, cantidades, clientes, mercados ni obligaciones de notificación.",
      ],
    };
    if (record) {
      downloadJsonFile({ ...base, record }, `RA-TRACE_${project.id.slice(0, 8)}_${record.ref}.json`);
      return;
    }
    downloadJsonFile({ ...base, records }, `RA-TRACE_${project.id.slice(0, 8)}.json`);
  }

  const affectedRows = useMemo(() => {
    if (!affected) return [];
    return records.filter((record) => {
      if (!record.customer) return false;
      if (affected.lot_id && record.lot_id) return record.lot_id === affected.lot_id;
      if (record.product !== affected.product) return false;
      const sameVariant = affected.variant_id && record.variant_id
        ? record.variant_id === affected.variant_id
        : !affected.variant_name || !record.variant_name || record.variant_name === affected.variant_name;
      return sameVariant && (!affected.batch_lot || record.batch_lot === affected.batch_lot);
    });
  }, [affected, records]);

  const lang = localStorage.getItem("rg_lang") || "en";
  const roleTitleKey = role === "importer"
    ? "trace.role_title.importer"
    : role === "distributor"
      ? "trace.role_title.distributor"
      : "trace.title";
  const roleSubtitleKey = role === "importer"
    ? "trace.role_sub.importer"
    : role === "distributor"
      ? "trace.role_sub.distributor"
      : "trace.sub";
  const newRecordLabel = role === "importer"
    ? t("trace.new_import")
    : role === "distributor"
      ? t("trace.new_delivery")
      : t("qms.new");

  return (
    <div className="space-y-5" data-testid="trace-tab">
      <div className="grid-card p-5">
        <h2 className="text-lg font-extrabold tracking-tight">{t(roleTitleKey)}</h2>
        <p className="text-sm text-[var(--text-2)] mt-1">{t(roleSubtitleKey)}</p>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="trace-dashboard">
        <Stat label={t("qms.total")} value={stats.total} testid="trace-stat-total" />
        <Stat label={t("trace.safety_actions")} value={stats.safety} accent="text-red-700" testid="trace-stat-safety" />
        <Stat label={t("trace.countries")} value={stats.countries} accent="text-blue-700" testid="trace-stat-countries" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("trace.search")}
            data-testid="trace-search"
            className="w-full border border-zinc-200 pl-8 pr-2 py-2 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
        </div>
        <select value={fAction} onChange={(e) => setFAction(e.target.value)} data-testid="trace-filter-action"
          className="border border-zinc-200 px-2 py-2 rounded-sm text-xs bg-white focus:border-blue-600 focus:outline-none">
          <option value="">{t("trace.filter_action")}</option>
          {CORRECTIVE_ACTIONS.map((c) => <option key={c} value={c}>{t(`trace.ca.${c.toLowerCase()}`)}</option>)}
        </select>
        <select value={fCountry} onChange={(e) => setFCountry(e.target.value)} data-testid="trace-filter-country"
          className="border border-zinc-200 px-2 py-2 rounded-sm text-xs bg-white focus:border-blue-600 focus:outline-none">
          <option value="">{t("trace.filter_country")}</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={() => downloadFile(`/projects/${project.id}/trace/export/xlsx`, `RA-TRACE_${project.id.slice(0, 8)}.xlsx`, "xlsx")}
          disabled={busy === "xlsx" || records.length === 0} data-testid="trace-export-xlsx"
          className="inline-flex items-center gap-1 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-2 rounded-sm text-xs font-semibold transition-colors disabled:opacity-40">
          <FileXls size={14} weight="bold" /> {busy === "xlsx" ? "…" : t("trace.export_xlsx")}
        </button>
        <button type="button" onClick={() => exportTraceJson()} disabled={!records.length}
          data-testid="trace-export-json"
          className="rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] disabled:opacity-40">
          {t("exports.download_history_json")}
        </button>
        <button onClick={startNew} data-testid="trace-new" className="btn-primary inline-flex items-center gap-1 px-3 py-2 text-xs">
          <Plus size={12} weight="bold" /> {newRecordLabel}
        </button>
      </div>

      {okMsg && (
        <div className="inline-flex items-center gap-1 text-sm text-emerald-700" data-testid="trace-ok">
          <CheckCircle size={14} weight="fill" /> {okMsg}
        </div>
      )}

      {editing !== null && (
        <div className="grid-card p-4 space-y-4 border-l-4 border-l-blue-600" data-testid="trace-editor">
          <div className="flex items-center justify-between">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
              {editing === "new" ? newRecordLabel : `${t("qms.edit")} · ${draft.ref || ""}`}
            </div>
            <button onClick={() => setEditing(null)} data-testid="trace-editor-close" className="p-1 hover:bg-zinc-100 rounded-sm"><X size={14} /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase text-zinc-600">{t("trace.supplier")}</label>
              <input list="trace-suppliers" value={draft.supplier || ""} onChange={(e) => setDraft({ ...draft, supplier: e.target.value })}
                data-testid="trace-f-supplier"
                className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm bg-white focus:border-blue-600 focus:outline-none" />
              <datalist id="trace-suppliers">
                {suppliers.map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
            {device ? (
              <>
                <Field label={t("trace.product")} value={device.name || draft.product} onChange={() => {}} testid="trace-f-product" readOnly />
                <div>
                  <label className="text-xs font-semibold uppercase text-[var(--text-2)]">{t("trace.variant")}</label>
                  <select
                    value={draft.variant_ref || ""}
                    required={editing === "new" && variantOptions.length > 0}
                    onChange={(event) => {
                      const selected = variantOptions.find((option) => option.ref === event.target.value);
                      setDraft({
                        ...draft,
                        variant_ref: event.target.value,
                        product: device.name || draft.product,
                        udi_di: selected?.udi || device.udi_di || "",
                      });
                    }}
                    data-testid="trace-f-variant"
                    className="mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[var(--text)]"
                  >
                    <option value="">{t("trace.variant_none")}</option>
                    {draft.variant_ref && !variantOptions.some((option) => option.ref === draft.variant_ref) && (
                      <option value={draft.variant_ref}>{draft.variant_name || t("trace.variant_legacy")}</option>
                    )}
                    {variantOptions.map((option) => (
                      <option key={option.ref} value={option.ref}>
                        {option.name}{option.model && option.model !== option.name ? ` · ${option.model}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <Field label={t("trace.product")} value={draft.product} onChange={(v) => setDraft({ ...draft, product: v })} testid="trace-f-product" />
            )}
            <Field label={t("trace.batch")} value={draft.batch_lot} onChange={(v) => setDraft({ ...draft, batch_lot: v })} testid="trace-f-batch" />
            <Field label="UDI-DI" value={draft.udi_di} onChange={(v) => setDraft({ ...draft, udi_di: v })} testid="trace-f-udi-di" readOnly={Boolean(device)} />
            <Field label="UDI-PI" value={draft.udi_pi} onChange={(v) => setDraft({ ...draft, udi_pi: v })} testid="trace-f-udi-pi" />
            <Field label={t("trace.serial")} value={draft.serial_number} onChange={(v) => setDraft({ ...draft, serial_number: v })} testid="trace-f-serial" />
            <Field label={t("trace.purchase_date")} type="date" value={draft.purchase_date} onChange={(v) => setDraft({ ...draft, purchase_date: v })} testid="trace-f-purchase" />
            <Field label={t("trace.sale_date")} type="date" value={draft.sale_date} onChange={(v) => setDraft({ ...draft, sale_date: v })} testid="trace-f-sale" />
            <Field label={t("trace.quantity")} type="number" value={draft.quantity} onChange={(v) => setDraft({ ...draft, quantity: v })} testid="trace-f-qty" />
            <Field label={t("trace.customer")} value={draft.customer} onChange={(v) => setDraft({ ...draft, customer: v })} testid="trace-f-customer" />
            <Field label={t("trace.customer_address")} value={draft.customer_address} onChange={(v) => setDraft({ ...draft, customer_address: v })} testid="trace-f-address" />
            <Field label={t("trace.country")} value={draft.country} onChange={(v) => setDraft({ ...draft, country: v })} testid="trace-f-country" />
            <div className="md:col-span-3">
              <Field label={t("trace.invoice")} value={draft.invoice_ref} onChange={(v) => setDraft({ ...draft, invoice_ref: v })} testid="trace-f-invoice" />
            </div>
          </div>
          <Area label={t("trace.comments")} value={draft.comments} onChange={(v) => setDraft({ ...draft, comments: v })} testid="trace-f-comments" />

          <div className="border border-amber-200 bg-amber-50/40 rounded-sm p-3 space-y-3">
            <div className="text-xs font-mono uppercase tracking-wider text-amber-800 font-bold">{t("trace.fs_section")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase text-zinc-600">{t("trace.corrective_action")}</label>
                <select value={draft.corrective_action || "None"} onChange={(e) => setDraft({ ...draft, corrective_action: e.target.value })}
                  data-testid="trace-f-ca"
                  className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm bg-white focus:border-blue-600 focus:outline-none">
                  {CORRECTIVE_ACTIONS.map((c) => <option key={c} value={c}>{t(`trace.ca.${c.toLowerCase()}`)}</option>)}
                </select>
              </div>
            </div>
            <Area label={t("trace.corrective_comments")} value={draft.corrective_comments} onChange={(v) => setDraft({ ...draft, corrective_comments: v })} testid="trace-f-ca-comments" />
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-zinc-100">
            <button onClick={save} disabled={saving || !draft.product.trim()} data-testid="trace-save"
              className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-40">
              <FloppyDisk size={14} weight="bold" /> {saving ? "…" : t("qms.save")}
            </button>
            <button onClick={() => setEditing(null)} data-testid="trace-cancel"
              className="border border-zinc-200 hover:bg-zinc-50 px-4 py-2 rounded-sm text-sm font-semibold transition-colors">
              {t("qms.cancel")}
            </button>
          </div>
        </div>
      )}

      {affected && (
        <div className="grid-card p-4 border-l-4 border-l-red-500 space-y-3" data-testid="trace-affected-panel">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-red-700 font-bold inline-flex items-center gap-1">
                <UsersThree size={14} weight="bold" /> {t("trace.affected_title")}
              </div>
              <div className="text-sm font-semibold mt-1">{affected.product}{affected.batch_lot ? ` · ${affected.batch_lot}` : ""} · {t(`trace.ca.${(affected.corrective_action || "None").toLowerCase()}`)}</div>
            </div>
            <button onClick={() => setAffected(null)} data-testid="trace-affected-close" className="p-1 hover:bg-zinc-100 rounded-sm"><X size={14} /></button>
          </div>
          {affectedRows.length === 0 ? (
            <div className="text-xs text-zinc-500 font-mono" data-testid="trace-affected-empty">{t("trace.affected_none")}</div>
          ) : (
            <table className="w-full text-sm" data-testid="trace-affected-list">
              <thead>
                <tr className="border-b border-zinc-200 text-left">
                  <Th>{t("qms.ref")}</Th>
                  <Th>{t("trace.customer")}</Th>
                  <Th>{t("trace.customer_address")}</Th>
                  <Th>{t("trace.country")}</Th>
                  <Th>{t("trace.quantity")}</Th>
                  <Th>{t("trace.sale_date")}</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {affectedRows.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100">
                    <td className="px-3 py-2 font-mono text-xs font-bold">{r.ref}</td>
                    <td className="px-3 py-2 text-xs font-semibold">{r.customer}</td>
                    <td className="px-3 py-2 text-xs">{r.customer_address || "—"}</td>
                    <td className="px-3 py-2 text-xs">{r.country || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.quantity || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.sale_date || "—"}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => downloadFile(`/projects/${project.id}/trace/${r.id}/letter/pdf?lang=${lang}`, `RA-LETTER_${r.ref}.pdf`, `letter-${r.id}`)}
                        disabled={busy === `letter-${r.id}`} data-testid={`trace-letter-${r.ref}`}
                        className="inline-flex items-center gap-1 border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 px-2.5 py-1.5 rounded-sm text-xs font-semibold transition-colors disabled:opacity-40">
                        <FilePdf size={12} weight="bold" /> {busy === `letter-${r.id}` ? "…" : t("trace.letter_btn")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="grid-card p-0 overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="text-xs text-zinc-500 font-mono py-8 text-center" data-testid="trace-empty">{t("qms.no_records")}</div>
        ) : (
          <table className="w-full text-sm" data-testid="trace-list">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <Th>{t("qms.ref")}</Th>
                <Th>{t("trace.product")}</Th>
                <Th>{t("trace.variant")}</Th>
                <Th>{t("trace.batch")}</Th>
                <Th>UDI-DI</Th>
                <Th>{role === "importer" ? t("trace.supplier") : t("trace.customer")}</Th>
                <Th>{t("trace.country")}</Th>
                <Th>{role === "importer" ? t("trace.purchase_date") : t("trace.sale_date")}</Th>
                <Th>{t("trace.quantity")}</Th>
                <Th>{t("trace.corrective_action")}</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((rec) => (
                <tr key={rec.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`trace-row-${rec.ref}`}>
                  <td className="px-3 py-2 font-mono text-xs font-bold">{rec.ref}</td>
                  <td className="px-3 py-2">{rec.product}</td>
                  <td className="px-3 py-2">{rec.variant_name || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{rec.batch_lot || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{rec.udi_di || "—"}</td>
                  <td className="px-3 py-2 text-xs">{(role === "importer" ? rec.supplier : rec.customer) || "—"}</td>
                  <td className="px-3 py-2 text-xs">{rec.country || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{(role === "importer" ? rec.purchase_date : rec.sale_date) || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{rec.quantity || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block border rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${CA_STYLE[rec.corrective_action] || CA_STYLE.None}`}>
                      {t(`trace.ca.${(rec.corrective_action || "None").toLowerCase()}`)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {(rec.corrective_action === "Recall" || rec.corrective_action === "FSCA") && (
                        <button onClick={() => setAffected(rec)} title={t("trace.affected_btn")} data-testid={`trace-affected-${rec.ref}`}
                          className="p-1.5 hover:bg-red-100 text-red-600 rounded-sm"><UsersThree size={14} weight="bold" /></button>
                      )}
                      <button onClick={() => startEdit(rec)} title={t("qms.edit")} data-testid={`trace-edit-${rec.ref}`}
                        className="p-1.5 hover:bg-blue-100 text-blue-700 rounded-sm"><PencilSimple size={14} weight="bold" /></button>
                      <button onClick={() => downloadFile(`/projects/${project.id}/trace/${rec.id}/pdf?lang=${lang}`, `RA-TRACE_${project.id.slice(0, 8)}_${rec.ref}.pdf`, rec.id)}
                        disabled={busy === rec.id} title={t("qms.download_pdf")} data-testid={`trace-pdf-${rec.ref}`}
                        className="p-1.5 hover:bg-blue-100 text-blue-700 rounded-sm disabled:opacity-40"><FilePdf size={14} weight="bold" /></button>
                      <button type="button" onClick={() => exportTraceJson(rec)} title={t("exports.download_json")} data-testid={`trace-json-${rec.ref}`}
                        className="rounded-sm p-1.5 text-zinc-700 hover:bg-zinc-100">
                        <span className="font-mono text-[9px] font-bold">JSON</span>
                      </button>
                      <button onClick={() => remove(rec)} title={t("qms.delete")} data-testid={`trace-del-${rec.ref}`}
                        className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"><Trash size={14} weight="bold" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-zinc-900", testid }) {
  return (
    <div className="grid-card p-3" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-2xl font-extrabold ${accent}`}>{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500 font-semibold">{children}</th>;
}

function Field({ label, value, onChange, type = "text", testid, readOnly = false }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} readOnly={readOnly}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none read-only:bg-[var(--surface-hover)]" />
    </div>
  );
}

function Area({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{label}</label>
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
    </div>
  );
}
